import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  FormControlLabel,
  Radio, // Import Radio for radio buttons
  RadioGroup, // Import RadioGroup for radio buttons
} from "@mui/material";
import SelectOptions from "../../components/ui/SelectOptions";
import { FormGrid } from "../../components/ui/FormGrid";
import { InputField } from "../../components/ui/Inputs";
import { SectionHeader } from "../../components/ui/SectionHeader";
import StudentDetail from "./../../assets/images/icons/studentdetails.png"; // Assuming this icon is suitable

interface Props {
  onNext: () => void;
}

interface Option {
  id: string | number;
  name?: string;
  label?: string;
}

interface Field {
  label: string;
  field_id: string;
  placeholder?: string;
  value: string;
  options?: Option[];
  mainLable?: string;
  action_label?: string;
}

interface Section {
  [key: string]: Field;
}

interface ApiData {
  product_application_form: {
    product_selection: Field;
    student_details: Section; // Keep existing student_details if needed elsewhere
    employment_details_section: Section; // New section for employment details
    company_address_section: Section; // New section for company address
  };
}

// Updated apiData to match the image's form structure
const apiData: ApiData = {
  product_application_form: {
    product_selection: {
      label: "How should we Employment you? (Mr./Ms./etc.)",
      field_id: "product_selection",
      options: [
        { id: 12, name: "KYC Mode" },
        { id: 16, name: "Type of Profile" },
        { id: 20, name: "Location" },
        { id: 28, name: "Customer Type" },
      ],
      value: "",
    },
    student_details: {
      // your existing student details fields (keeping this as is for context, but not used in this specific component's UI)
    },
    employment_details_section: {
      employment_type: {
        label: "Employment Type",
        field_id: "employment_type",
        options: [
          { id: "salaried", name: "Salaried" },
          { id: "self_employed", name: "Self-Employed" },
          { id: "unemployed", name: "Unemployed" },
        ],
        value: "",
      },
      monthly_obligation: {
        label: "Monthly Obligation",
        field_id: "monthly_obligation",
        placeholder: "Enter Monthly Obligation",
        value: "",
      },
      company_name: {
        label: "Company Name",
        field_id: "company_name",
        placeholder: "Enter Company Name",
        value: "",
      },
      designation: {
        label: "Designation",
        field_id: "designation",
        placeholder: "Enter Designation",
        value: "",
      },
      monthly_income: {
        label: "Monthly Income",
        field_id: "monthly_income",
        placeholder: "Enter Monthly Income",
        value: "",
      },
    },
    company_address_section: {
      address_line_1: {
        label: "Address Line 1",
        field_id: "address_line_1",
        placeholder: "Enter Address Line 1",
        value: "",
      },
      address_line_2: {
        label: "Address Line 2",
        field_id: "address_line_2",
        placeholder: "Enter Address Line 2",
        value: "",
      },
      address_line_3: {
        label: "Address Line 3",
        field_id: "address_line_3",
        placeholder: "Enter Address Line 3",
        value: "",
      },
      pincode: {
        label: "Pincode",
        field_id: "pincode",
        placeholder: "Enter Pincode",
        value: "",
      },
      city: {
        label: "City",
        field_id: "city",
        placeholder: "Enter City",
        value: "",
      },
      state: {
        label: "State",
        field_id: "state",
        placeholder: "Enter State",
        value: "",
      },
      country: {
        label: "Country",
        field_id: "country",
        placeholder: "Enter Country",
        value: "",
      },
    },
  },
};

// Type guard to check if field has options
function hasOptions(field: Field): field is Field & { options: Option[] } {
  return Array.isArray(field.options);
}

const EmploymentDetails: React.FC<Props> = ({ onNext }) => {
  const employmentDetailsFields =
    apiData.product_application_form.employment_details_section;
  const companyAddressFields =
    apiData.product_application_form.company_address_section;

  // State for "Are you employed?" radio button
  const [isEmployed, setIsEmployed] = useState<string | null>(null); // 'Yes', 'No', or null

  // State for Employment Details
  const [employmentData, setEmploymentData] = useState<{
    [key: string]: string;
  }>(() => {
    const initial: { [key: string]: string } = {};
    Object.values(employmentDetailsFields).forEach((f) => {
      initial[f.field_id] = f.value || "";
    });
    return initial;
  });

  // State for Company Address
  const [companyAddressData, setCompanyAddressData] = useState<{
    [key: string]: string;
  }>(() => {
    const initial: { [key: string]: string } = {};
    Object.values(companyAddressFields).forEach((f) => {
      initial[f.field_id] = f.value || "";
    });
    return initial;
  });

  // Handler for "Are you employed?" radio button change
  const handleIsEmployedChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsEmployed(event.target.value);
  };

  // Handlers for Employment Details input change
  const handleEmploymentInputChange =
    (field: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setEmploymentData((prev) => ({ ...prev, [field]: value }));
    };

  // Handlers for Employment Details select change
  const handleEmploymentSelectChange =
    (field: string) => (value: string | number) => {
      setEmploymentData((prev) => ({ ...prev, [field]: value.toString() }));
    };

  // Handlers for Company Address input change
  const handleCompanyAddressInputChange =
    (field: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setCompanyAddressData((prev) => ({ ...prev, [field]: value }));
    };

  // Prepare input fields for FormGrid
  const prepareInputFields = (
    fields: Section,
    values: { [key: string]: string },
    onChange: (
      field: string
    ) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  ) =>
    Object.values(fields)
      .filter((f) => !hasOptions(f)) // Filter out fields that have options (dropdowns)
      .map((field) => ({
        label: field.label,
        component: (
          <InputField
            key={field.field_id}
            fullWidth
            placeholder={field.placeholder || ""}
            value={values[field.field_id]}
            onChange={onChange(field.field_id)}
            variant="outlined"
          />
        ),
      }));

  // Prepare select fields for FormGrid
  const prepareSelectFields = (
    fields: Section,
    values: { [key: string]: string },
    onChange: (field: string) => (value: string | number) => void
  ) =>
    Object.values(fields)
      .filter(hasOptions)
      .map((field) => ({
        label: field.label, // Use field.label for the SelectOptions component
        component: (
          <SelectOptions
            key={field.field_id}
            label={field.label}
            value={values[field.field_id]}
            onChange={onChange(field.field_id)}
            options={field.options!.map((opt) => ({
              value: opt.id.toString(),
              label: opt.name || opt.label || "",
            }))}
            isMandatory={false}
          />
        ),
      }));

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <SectionHeader
          icon={<img src={StudentDetail} alt="Employment Icon" />}
          title="Employment Details"
          subtitle="Employment Related Details"
          completion="0/9" // This would ideally be dynamic based on form completion
        />
      </Box>

      {/* "Are you employed?" radio buttons */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Typography variant="body1" sx={{ mr: 2 }}>
          Are you employed?
        </Typography>
        <RadioGroup row value={isEmployed} onChange={handleIsEmployedChange}>
          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="No" control={<Radio />} label="No" />
        </RadioGroup>
      </Box>

      {/* Employment Details Section */}
      {isEmployed === "Yes" && ( // Only show if "Yes" is selected
        <>
          {/* Employment Type Select Field */}
          <FormGrid
            fields={prepareSelectFields(
              { employment_type: employmentDetailsFields.employment_type }, // Pass only employment_type
              employmentData,
              handleEmploymentSelectChange
            )}
          />

          {/* Other Employment Details Input Fields */}
          <FormGrid
            fields={prepareInputFields(
              {
                monthly_obligation: employmentDetailsFields.monthly_obligation,
                company_name: employmentDetailsFields.company_name,
                designation: employmentDetailsFields.designation,
                monthly_income: employmentDetailsFields.monthly_income,
              },
              employmentData,
              handleEmploymentInputChange
            )}
          />

          {/* Company Address Section Header */}
          <Box sx={{ mb: 2, mt: 4 }}>
            <SectionHeader
              icon={<img src={StudentDetail} alt="Company Address Icon" />}
              title="Company Address"
              subtitle="Company Address Related Details"
              completion="0/9" // This would ideally be dynamic
            />
          </Box>

          {/* Company Address Input Fields */}
          <FormGrid
            fields={prepareInputFields(
              companyAddressFields,
              companyAddressData,
              handleCompanyAddressInputChange
            )}
          />
        </>
      )}

      {/* Example Next button (from original code, keep as needed) */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        {/* <Button variant="contained" onClick={onNext}>
          Next
        </Button> */}
      </Box>
    </Box>
  );
};

export default EmploymentDetails;
